// src/components/Onboarding.jsx
// First-run welcome overlay. Shows once, never again after dismissed.
// Stored in localStorage so it survives app restarts.

import { useState, useEffect } from 'react'

const STORAGE_KEY = 'netwoke-onboarding-done'

const STEPS = [
  {
    icon: '🔌',
    title: "You're seeing your Mac's live network activity",
    body: "Every time an app on your Mac connects to the internet — to check email, sync files, or load a webpage — it shows up here. You're not doing anything, this is just what's always been happening in the background.",
    color: '#00d4b8',
  },
  {
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3ecf8e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>,
    title: "Green lock means your data is private",
    body: "When a connection shows a green lock and says 'Encrypted' it means your data is scrambled and nobody can read it. When you see amber and 'Not Encrypted' it means your data is sent in plain text — like a postcard anyone can read.",
    color: '#3ecf8e',
  },
  {
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4d9de0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
    title: "Blue alerts provide useful information",
    body: "Blue alerts are informational - they tell you about interesting network activity but don't require immediate action. These might include unusual port connections or general network observations that are good to be aware of.",
    color: '#4d9de0',
  },
  {
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f5a623" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    title: "Alerts tell you when something needs attention",
    body: "Netwoke watches for unusual behavior automatically. If an app connects somewhere unexpected, sends unencrypted data, or does something suspicious — you'll get a plain-English notification explaining what happened and what to do about it.",
    color: '#f5a623',
  },
  {
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17M12 17"/></svg>,
    title: "Red connections need immediate attention",
    body: "When you see a red connection, it means something is potentially dangerous or misconfigured. Netwoke monitors for connections to suspicious countries and known malicious servers. Click on it immediately to investigate or use the 'Kill Process' button to stop it right away.",
    color: '#ef4444',
  },
  {
    icon: '🤖',
    title: "Ask AI to explain anything confusing",
    body: "See something you don't recognize? Click on any connection, expand it, and tap 'Ask AI to explain this.' You'll get a plain-English answer about what it is, whether it's safe, and what to do — no technical knowledge needed.",
    color: '#4d9de0',
  },
  {
    icon: '📈',
    title: "Real-time monitoring dashboard",
    body: "The main dashboard shows all your active connections, apps, and alerts in real-time. You can kill suspicious connections, block problematic IPs, and see exactly what each app is doing online.",
    color: '#00d4b8',
  },
  {
    icon: '️🛡️',
    title: "Privacy protection focus",
    body: "Netwoke focuses on protecting your privacy. We don't track or store your personal data — everything happens locally on your Mac. Your network activity never leaves your device.",
    color: '#3ecf8e',
  },
  {
    icon: '⚙️',
    title: "Simple, powerful controls",
    body: "Kill apps that misbehave, block suspicious IPs, and dismiss alerts with one click. Netwoke puts you in control of your network privacy without overwhelming complexity.",
    color: '#f5a623',
  },
]

export default function Onboarding({ onDone }) {
  const [step, setStep] = useState(0)
  const [exiting, setExiting] = useState(false)

  const isLast = step === STEPS.length - 1
  const current = STEPS[step]

  const handleNext = () => {
    if (isLast) {
      handleDone()
    } else {
      setStep(s => s + 1)
    }
  }

  const handleDone = () => {
    setExiting(true)
    localStorage.setItem(STORAGE_KEY, '1')
    setTimeout(() => onDone?.(), 400)
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(4,6,13,0.85)', backdropFilter: 'blur(12px)',
      opacity: exiting ? 0 : 1,
      transition: 'opacity 0.4s ease',
    }}>
      <div style={{
        background: '#0d1220',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 20, padding: '36px 32px 28px',
        maxWidth: 420, width: '90%',
        boxShadow: '0 40px 100px rgba(0,0,0,0.7)',
        position: 'relative',
        transform: exiting ? 'scale(0.95)' : 'scale(1)',
        transition: 'transform 0.4s ease',
      }}>
        {/* Top accent bar */}
        <div style={{
          position: 'absolute', top: 0, left: 32, right: 32, height: 2,
          background: `linear-gradient(90deg, ${current.color}, transparent)`,
          borderRadius: '0 0 2px 2px',
        }} />

        {/* Skip */}
        <button onClick={handleDone} style={{
          position: 'absolute', top: 16, right: 16,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 11, color: 'rgba(220,232,245,0.3)',
          fontFamily: 'var(--font-mono)',
          transition: 'color 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(220,232,245,0.6)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(220,232,245,0.3)'}
        >Skip</button>

        {/* Step dots */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 20 : 6,
              height: 6, borderRadius: 3,
              background: i === step ? current.color : 'rgba(255,255,255,0.12)',
              transition: 'all 0.3s ease',
            }} />
          ))}
        </div>

        {/* Icon */}
        <div style={{
          fontSize: 40, marginBottom: 16, lineHeight: 1,
          filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
        }}>
          {current.icon}
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: 18, fontWeight: 700, lineHeight: 1.3,
          letterSpacing: '-0.015em', marginBottom: 12,
          color: '#dce8f5',
          fontFamily: 'var(--font-mono)',
        }}>
          {current.title}
        </h2>

        {/* Body */}
        <p style={{
          fontSize: 14, color: 'rgba(220,232,245,0.65)',
          lineHeight: 1.75, fontWeight: 300, marginBottom: 32,
          fontFamily: 'var(--font-mono)',
        }}>
          {current.body}
        </p>

        {/* Button */}
        <button onClick={handleNext} style={{
          width: '100%', padding: '13px',
          background: `linear-gradient(135deg, ${current.color}, #4d9de0)`,
          border: 'none', borderRadius: 12,
          fontSize: 15, fontWeight: 700, color: '#04060d',
          cursor: 'pointer', fontFamily: 'var(--font-mono)',
          letterSpacing: '-0.01em',
          boxShadow: `0 0 24px ${current.color}30`,
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 0 36px ${current.color}50` }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 0 24px ${current.color}30` }}
        >
          {isLast ? "Got it, let's go →" : 'Next →'}
        </button>

        {/* Step counter */}
        <div style={{
          textAlign: 'center', marginTop: 14,
          fontSize: 10, color: 'rgba(220,232,245,0.25)',
          fontFamily: "'IBM Plex Mono', monospace",
        }}>
          {step + 1} of {STEPS.length}
        </div>
      </div>
    </div>
  )
}

// Hook to always show onboarding
export function useOnboarding() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    // Always show onboarding after small delay
    setTimeout(() => setShow(true), 800)
  }, [])

  return [show, () => setShow(false)]
}
